import { useTranslate } from '@ur-solutions/react-hooks'

import type { IssueActionLog } from '../../types.graphl'
import { ActionLogCard } from './action-log-card'
import { ActionLogEntry } from './action-log-entry'
import {
  ActionLogEmpty,
  ActionLogError,
  ActionLogSkeleton,
} from './action-log-states'

/**
 * Timeline view of an issue's action log. Newest entry first.
 *
 * Each backend row degrades gracefully:
 * - has `metadata.changed_fields` -> expandable field diff (old -> new)
 * - no changed_fields (legacy / null metadata) -> plain header line
 * - EXPORTED -> header line + export summary chip
 */
type Props = {
  logs: IssueActionLog[]
  loading?: boolean
  error?: boolean
}

export const IssueActionLogTimeline = ({ logs, loading, error }: Props) => {
  const t = useTranslate({ title: 'issues.action-log' })

  let body
  if (error && logs.length === 0) {
    body = <ActionLogError />
  } else if (loading && logs.length === 0) {
    body = <ActionLogSkeleton />
  } else if (logs.length === 0) {
    body = <ActionLogEmpty />
  } else {
    // Backend returns oldest-first; show newest-first like a feed.
    const ordered = [...logs].reverse()
    body = (
      <div className="flex flex-col">
        {ordered.map((node, i) => (
          <ActionLogEntry
            key={node.id}
            node={node}
            isLast={i === ordered.length - 1}
          />
        ))}
      </div>
    )
  }

  return <ActionLogCard title={t.title}>{body}</ActionLogCard>
}
